import React, { useRef, useMemo } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import * as THREE from 'three';

// ============================================
// NEURAL BRAIN NETWORK - Cerebro 3D con IA
// Cerebro humano 3D con neuronas interconectadas
// ============================================

// 1. NEURONA individual con 3 capas (núcleo, halo, glow)
function Neuron({ position, color, baseSize, activationPhase, onActivate }) {
  const coreRef = useRef();
  const haloRef = useRef();
  const glowRef = useRef();

  useFrame((state) => {
    if (!coreRef.current || !haloRef.current || !glowRef.current) return;
    const time = state.clock.getElapsedTime();

    // Pulsación continua
    const pulse = Math.sin(time * 2 + activationPhase) * 0.5 + 0.5;
    const scale = baseSize * (0.9 + pulse * 0.3);

    coreRef.current.scale.setScalar(scale);
    haloRef.current.scale.setScalar(scale * 1.4);
    glowRef.current.scale.setScalar(scale * 2);

    // Efecto de activación (neuronas se iluminan en cascada)
    const activation = Math.sin(time * 0.5 + activationPhase) * 0.5 + 0.5;
    if (coreRef.current.material) {
      coreRef.current.material.emissiveIntensity = 1 + activation * 2;
    }
    if (haloRef.current.material) {
      haloRef.current.material.opacity = 0.3 + activation * 0.4;
    }
    if (glowRef.current.material) {
      glowRef.current.material.opacity = 0.1 + activation * 0.2;
    }
  });

  return (
    <group position={position}>
      {/* Núcleo de la neurona */}
      <mesh ref={coreRef}>
        <sphereGeometry args={[baseSize, 16, 16]} />
        <meshStandardMaterial
          color={color}
          emissive={color}
          emissiveIntensity={1.5}
          metalness={0.3}
          roughness={0.4}
        />
      </mesh>

      {/* Halo exterior */}
      <mesh ref={haloRef}>
        <sphereGeometry args={[baseSize * 1.3, 12, 12]} />
        <meshBasicMaterial
          color={color}
          transparent
          opacity={0.4}
          blending={THREE.AdditiveBlending}
        />
      </mesh>

      {/* Glow difuso */}
      <mesh ref={glowRef}>
        <sphereGeometry args={[baseSize * 1.8, 8, 8]} />
        <meshBasicMaterial
          color={color}
          transparent
          opacity={0.15}
          blending={THREE.AdditiveBlending}
        />
      </mesh>
    </group>
  );
}

// 2. ESTRUCTURA DEL CEREBRO con 700 neuronas distribuidas en forma cerebral
function BrainStructure() {
  const brainRef = useRef();

  // Generar posiciones de neuronas en forma de cerebro
  const neurons = useMemo(() => {
    const neuronArray = [];
    const totalNeurons = 700;

    // Hemisferio izquierdo y derecho
    for (let i = 0; i < totalNeurons; i++) {
      const side = i < totalNeurons / 2 ? -1 : 1; // -1 izquierda, 1 derecha

      // Distribución en forma de cerebro (elipsoide)
      const phi = Math.random() * Math.PI; // 0 a PI (arriba-abajo)
      const theta = Math.random() * Math.PI; // Solo medio círculo por hemisferio

      const radiusX = 6 + Math.random() * 2; // Ancho del cerebro
      const radiusY = 7 + Math.random() * 1; // Alto del cerebro
      const radiusZ = 5 + Math.random() * 1.5; // Profundidad

      const x = side * radiusX * Math.sin(phi) * Math.cos(theta);
      const y = radiusY * Math.cos(phi);
      const z = radiusZ * Math.sin(phi) * Math.sin(theta);

      // Evitar neuronas en el centro (corpus callosum)
      if (Math.abs(x) < 1.5 && Math.abs(y) < 2) continue;

      // Color gradiente cyan → verde basado en altura
      const t = (y + 7) / 14; // Normalizar 0-1
      const color = new THREE.Color().setHSL(0.5 + t * 0.1, 1, 0.5);

      neuronArray.push({
        position: new THREE.Vector3(x, y, z),
        color: `#${color.getHexString()}`,
        size: 0.08 + Math.random() * 0.06,
        activationPhase: Math.random() * Math.PI * 2,
      });
    }

    return neuronArray;
  }, []);

  useFrame((state) => {
    if (!brainRef.current) return;
    const time = state.clock.getElapsedTime();

    // Movimiento lateral lento (izquierda-derecha)
    brainRef.current.position.x = Math.sin(time * 0.2) * 8; // Oscila de -8 a +8 lentamente

    // Rotación suave del cerebro completo (360°)
    brainRef.current.rotation.y = time * 0.15;
    brainRef.current.rotation.x = Math.sin(time * 0.1) * 0.1;
  });

  return (
    <group ref={brainRef}>
      {neurons.map((neuron, i) => (
        <Neuron
          key={i}
          position={neuron.position}
          color={neuron.color}
          baseSize={neuron.size}
          activationPhase={neuron.activationPhase}
        />
      ))}
    </group>
  );
}

// 3. CONEXIONES SINÁPTICAS entre neuronas cercanas
function SynapticConnections() {
  const connectionsRef = useRef();

  const connections = useMemo(() => {
    const lines = [];
    const neuronCount = 150; // Subset de neuronas para conexiones

    // Generar posiciones similares a las del cerebro
    const positions = [];
    for (let i = 0; i < neuronCount; i++) {
      const side = i < neuronCount / 2 ? -1 : 1;
      const phi = Math.random() * Math.PI;
      const theta = Math.random() * Math.PI;

      const radiusX = 6 + Math.random() * 2;
      const radiusY = 7 + Math.random() * 1;
      const radiusZ = 5 + Math.random() * 1.5;

      const x = side * radiusX * Math.sin(phi) * Math.cos(theta);
      const y = radiusY * Math.cos(phi);
      const z = radiusZ * Math.sin(phi) * Math.sin(theta);

      if (Math.abs(x) < 1.5 && Math.abs(y) < 2) continue;

      positions.push(new THREE.Vector3(x, y, z));
    }

    // Conectar neuronas cercanas
    for (let i = 0; i < positions.length; i++) {
      const maxConnections = 3 + Math.floor(Math.random() * 4); // 3-6 conexiones
      let connectionCount = 0;

      for (let j = 0; j < positions.length; j++) {
        if (i === j) continue;

        const distance = positions[i].distanceTo(positions[j]);

        // Solo conectar neuronas cercanas (distancia < 3)
        if (distance < 3 && connectionCount < maxConnections) {
          lines.push({
            start: positions[i],
            end: positions[j],
            phase: Math.random() * Math.PI * 2,
          });
          connectionCount++;
        }
      }
    }

    return lines;
  }, []);

  useFrame((state) => {
    if (!connectionsRef.current) return;
    const time = state.clock.getElapsedTime();

    // Movimiento lateral sincronizado con el cerebro
    connectionsRef.current.position.x = Math.sin(time * 0.2) * 8;

    // Rotación sincronizada con el cerebro
    connectionsRef.current.rotation.y = time * 0.15;
    connectionsRef.current.rotation.x = Math.sin(time * 0.1) * 0.1;

    // Pulsación de opacidad en las conexiones
    connectionsRef.current.children.forEach((line, i) => {
      if (line.material) {
        const pulse = Math.sin(time * 2 + connections[i].phase) * 0.5 + 0.5;
        line.material.opacity = 0.1 + pulse * 0.2;
      }
    });
  });

  return (
    <group ref={connectionsRef}>
      {connections.map((conn, i) => {
        const points = [conn.start, conn.end];
        const geometry = new THREE.BufferGeometry().setFromPoints(points);

        return (
          <line key={i} geometry={geometry}>
            <lineBasicMaterial
              color="#ffffff"
              transparent
              opacity={0.2}
              blending={THREE.AdditiveBlending}
            />
          </line>
        );
      })}
    </group>
  );
}

// 4. PULSOS ELÉCTRICOS viajando por las conexiones
function ElectricPulses() {
  const pulsesRef = useRef();

  const pulses = useMemo(() => {
    const pulseArray = [];
    const pulseCount = 100;

    for (let i = 0; i < pulseCount; i++) {
      // Posición aleatoria en el espacio del cerebro
      const side = Math.random() > 0.5 ? -1 : 1;
      const phi = Math.random() * Math.PI;
      const theta = Math.random() * Math.PI;

      const radiusX = 6 + Math.random() * 2;
      const radiusY = 7 + Math.random() * 1;
      const radiusZ = 5 + Math.random() * 1.5;

      const x = side * radiusX * Math.sin(phi) * Math.cos(theta);
      const y = radiusY * Math.cos(phi);
      const z = radiusZ * Math.sin(phi) * Math.sin(theta);

      pulseArray.push({
        position: new THREE.Vector3(x, y, z),
        velocity: new THREE.Vector3(
          (Math.random() - 0.5) * 0.05,
          (Math.random() - 0.5) * 0.05,
          (Math.random() - 0.5) * 0.05
        ),
        phase: Math.random() * Math.PI * 2,
      });
    }

    return pulseArray;
  }, []);

  useFrame((state) => {
    if (!pulsesRef.current) return;
    const time = state.clock.getElapsedTime();

    // Movimiento lateral sincronizado con el cerebro
    pulsesRef.current.position.x = Math.sin(time * 0.2) * 8;

    // Rotación sincronizada
    pulsesRef.current.rotation.y = time * 0.15;
    pulsesRef.current.rotation.x = Math.sin(time * 0.1) * 0.1;

    // Actualizar posición de pulsos
    pulsesRef.current.children.forEach((pulse, i) => {
      const data = pulses[i];

      // Mover pulso
      data.position.add(data.velocity);

      // Rebote en los límites del cerebro
      if (Math.abs(data.position.x) > 8) data.velocity.x *= -1;
      if (Math.abs(data.position.y) > 8) data.velocity.y *= -1;
      if (Math.abs(data.position.z) > 7) data.velocity.z *= -1;

      pulse.position.copy(data.position);

      // Pulsación de brillo
      if (pulse.material) {
        const pulsation = Math.sin(time * 4 + data.phase) * 0.5 + 0.5;
        pulse.material.emissiveIntensity = 2 + pulsation * 3;
      }
    });
  });

  return (
    <group ref={pulsesRef}>
      {pulses.map((pulse, i) => (
        <mesh key={i} position={pulse.position}>
          <sphereGeometry args={[0.04, 8, 8]} />
          <meshStandardMaterial
            color="#0099ff"
            emissive="#0099ff"
            emissiveIntensity={3}
            transparent
            opacity={0.9}
          />
        </mesh>
      ))}
    </group>
  );
}

// 5. ONDAS DE PENSAMIENTO expandiéndose desde el centro
function ThoughtWaves() {
  const wavesRef = useRef();

  useFrame((state) => {
    const time = state.clock.getElapsedTime();

    if (wavesRef.current) {
      // Movimiento lateral sincronizado con el cerebro
      wavesRef.current.position.x = Math.sin(time * 0.2) * 8;

      // Rotación sincronizada
      wavesRef.current.rotation.y = time * 0.15;
      wavesRef.current.rotation.x = Math.sin(time * 0.1) * 0.1;

      wavesRef.current.children.forEach((wave, i) => {
        // Expansión continua desde el centro
        const scale = ((time * 0.4 + i * 0.7) % 4) + 1;
        wave.scale.set(scale, scale, scale);

        // Fade out mientras se expande
        if (wave.material) {
          wave.material.opacity = Math.max(0, 0.4 - (scale - 1) * 0.1);
        }

        // Rotación de las ondas individuales
        wave.rotation.y = time * 0.2 + i;
        wave.rotation.x = Math.sin(time * 0.3 + i) * 0.2;
      });
    }
  });

  return (
    <group ref={wavesRef}>
      {[0, 1, 2, 3].map((i) => (
        <mesh key={i}>
          <sphereGeometry args={[1, 32, 32]} />
          <meshBasicMaterial
            color="#00ffff"
            transparent
            opacity={0.3}
            wireframe
            blending={THREE.AdditiveBlending}
          />
        </mesh>
      ))}
    </group>
  );
}

// 6. PARTÍCULAS DE DATOS flotando alrededor del cerebro
function DataParticles() {
  const particlesRef = useRef();

  const geometry = useMemo(() => {
    const count = 1500;
    const positions = new Float32Array(count * 3);
    const colors = new Float32Array(count * 3);

    for (let i = 0; i < count; i++) {
      // Distribución esférica alrededor del cerebro
      const radius = 10 + Math.random() * 8;
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.random() * Math.PI;

      positions[i * 3] = radius * Math.sin(phi) * Math.cos(theta);
      positions[i * 3 + 1] = radius * Math.sin(phi) * Math.sin(theta);
      positions[i * 3 + 2] = radius * Math.cos(phi);

      // Color gradiente cyan → verde
      const t = Math.random();
      colors[i * 3] = 0; // R
      colors[i * 3 + 1] = t * 0.6 + 0.4; // G (0.4-1.0)
      colors[i * 3 + 2] = 1 - t * 0.5; // B (1.0-0.5)
    }

    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geo.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    return geo;
  }, []);

  useFrame((state) => {
    if (!particlesRef.current) return;
    const time = state.clock.getElapsedTime();

    // Movimiento lateral sincronizado con el cerebro
    particlesRef.current.position.x = Math.sin(time * 0.2) * 8;

    // Rotación lenta del campo de partículas
    particlesRef.current.rotation.y = time * 0.08;
    particlesRef.current.rotation.x = Math.sin(time * 0.05) * 0.2;

    // Actualizar posiciones (efecto de flotación)
    const positions = particlesRef.current.geometry.attributes.position.array;
    for (let i = 0; i < positions.length; i += 3) {
      positions[i + 1] += Math.sin(time + i) * 0.005;
    }
    particlesRef.current.geometry.attributes.position.needsUpdate = true;
  });

  return (
    <points ref={particlesRef} geometry={geometry}>
      <pointsMaterial
        size={0.05}
        vertexColors
        transparent
        opacity={0.6}
        sizeAttenuation
        blending={THREE.AdditiveBlending}
      />
    </points>
  );
}

// ESCENA PRINCIPAL DEL CEREBRO
function NeuralBrainScene() {
  return (
    <>
      {/* Iluminación */}
      <ambientLight intensity={0.4} />

      {/* Luces principales */}
      <pointLight position={[10, 10, 10]} intensity={2} color="#00ffff" />
      <pointLight position={[-10, 10, 10]} intensity={2} color="#00ff88" />
      <pointLight position={[0, -10, 8]} intensity={1.5} color="#0099ff" />

      {/* Spotlight desde arriba */}
      <spotLight
        position={[0, 15, 0]}
        angle={0.6}
        penumbra={1}
        intensity={2}
        color="#00d9ff"
      />

      {/* Componentes del cerebro */}
      <BrainStructure />
      <SynapticConnections />
      <ElectricPulses />
      <ThoughtWaves />
      <DataParticles />
    </>
  );
}

// COMPONENTE EXPORTABLE
export default function NeuralBrain3D() {
  return (
    <Canvas
      camera={{ position: [0, 0, 20], fov: 75 }}
      gl={{
        antialias: true,
        alpha: true,
        powerPreference: 'high-performance',
      }}
      dpr={[1, 2]}
    >
      {/* Fondo oscuro */}
      <color attach="background" args={['#000814']} />

      {/* Niebla azul para profundidad */}
      <fog attach="fog" args={['#001427', 15, 50]} />

      {/* Escena del cerebro */}
      <NeuralBrainScene />
    </Canvas>
  );
}
